// FNP-1500: the name derivation, and the base-name rules that gate it.
//
// ⚠️ THIS IS A MIRROR, NOT A NEW CONVENTION. The authority is
// docs/rules/CONVENTIONS.md § "Derived names", implemented server-side in
// console-api's repo-names.ts and resource-names.ts. If those change, this
// changes with them — a check that derives different names than provisioning
// does is worse than no check, because it reports FREE for a name nobody will
// ever create and stays silent about the one that collides.
//
// ⚠️ FNP-1557: THAT IS EXACTLY WHAT HAPPENED. The comment above was already
// here while this file dropped `repoNameSuffix` on the floor, so it asserted a
// fidelity it did not have and nothing checked the claim. The mirror is now
// pinned by fnp-1557-derived-names-match-orchestrator.test.ts, which
// transcribes the server's derivation and fails when the two disagree. Keep
// that test honest rather than trusting this paragraph.

import {
    BaseNameViolation,
    DerivedName,
    TargetStage,
} from '../types';

// Appending '-app' to a name that already ends in it would derive '-app-app'
// and '-app-app-infra'. The API rejects such a name rather than trimming it
// silently, because a name that quietly becomes something else is its own
// defect (FNP-990). We reject it here too, so the drawer says so before the
// user submits.
export const RESERVED_NAME_SUFFIX = '-app';

// The deployment bucket sets this ceiling: `<base>-infra-<accountId>-ui` adds
// 22 characters against S3's limit of 63.
//
// ⚠️ Nothing enforced this before FNP-1497, and exceeding it fails inside the
// PROVISIONED application's own pipeline — two repos and two pipelines after
// the mistake was made.
export const MAX_BASE_NAME_LENGTH = 41;

// A CodeCommit repository name and an S3 bucket name must both survive this.
// Lowercase alphanumerics and hyphens, starting and ending alphanumeric.
const VALID_BASE_NAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Every rule a base name must satisfy before any lookup is worth running.
//
// Returns ALL violations rather than the first: the drawer shows them together,
// and fixing a name one refusal at a time is the experience FNP-1497 complained
// about.
export const validateBaseName = (baseName: string): BaseNameViolation[] => {
    const violations: BaseNameViolation[] = [];
    const trimmed = baseName.trim();

    if (trimmed.length === 0) {
        return [{
            rule: 'EMPTY',
            message: 'Enter a base name.',
        }];
    }

    // Case-folded: 'Foo-App' derives the same repo name as 'foo-app'.
    if (trimmed.toLowerCase().endsWith(RESERVED_NAME_SUFFIX)) {
        const suggestion = trimmed.slice(0, -RESERVED_NAME_SUFFIX.length);
        violations.push({
            rule: 'RESERVED_SUFFIX',
            message: `A base name must not end in "${RESERVED_NAME_SUFFIX}": the `
                + 'source and infra repository names are derived by appending '
                + `"-app" and "-app-infra", which would produce "${trimmed}-app". `
                + `Use "${suggestion}" instead.`,
        });
    }

    if (trimmed.length > MAX_BASE_NAME_LENGTH) {
        violations.push({
            rule: 'TOO_LONG',
            message: `A base name is at most ${MAX_BASE_NAME_LENGTH} characters; `
                + `"${trimmed}" is ${trimmed.length}. The deployment bucket sets `
                + 'the ceiling: "<base>-infra-<accountId>-ui" adds 22 characters '
                + "against S3's limit of 63.",
        });
    }

    if (!VALID_BASE_NAME.test(trimmed)) {
        violations.push({
            rule: 'INVALID_CHARACTERS',
            message: 'A base name may contain only lowercase letters, digits and '
                + 'hyphens, and must start and end with a letter or digit.',
        });
    }

    return violations;
};

// Every name provisioning will derive from `baseName`.
//
// The account-independent names (two repos, two pipelines) are always derived.
// The stack and bucket names are per-stage, so they appear once per entry in
// `targetStages` — and not at all when none is given, because a stack name
// without a stage would be a name nobody provisions.
//
// ═══ FNP-1557: `repoNameSuffix` IS PLAYBOOK DATA, NOT A STAGE RULE ═══
//
// The orchestrator builds both repository names as `${base}${suffix}-app` and
// `${base}${suffix}-app-infra`, where the suffix is the SELECTED PLAYBOOK's
// `repoNameSuffix` step parameter — see `deriveRepoNames` in console-api and
// its caller in create-application-handler's `dispatch`, which reads
// `parameters?.repoNameSuffix ?? ''`.
//
// 🛑 DO NOT COMPUTE THIS FROM THE STAGE DROPDOWN. It is tempting, because the
// only provisioning playbook that exists today sets `-dev` and the drawer's
// stage happens to be `dev`. That correspondence is a coincidence of the
// current data, not a rule: the parameter is free-form JSON authored per step
// in the console, so a playbook whose steps said `-staging` would create
// `-staging` names no matter which stage the operator picked. Deriving the
// suffix here instead of reading it is how this check drifted from the
// orchestrator in the first place.
//
// Absent behaves as empty, matching the handler's `?? ''` — a prod playbook
// sets no suffix deliberately.
export const deriveNames = (
    baseName: string,
    targetStages: TargetStage[] = [],
    repoNameSuffix = '',
): DerivedName[] => {
    const base = baseName.trim();
    // The stem both repository names are built on. The stack and bucket names
    // below deliberately do NOT use it — see the comment at the loop.
    const repoBase = `${base}${repoNameSuffix}`;

    const sourceRepo = `${repoBase}-app`;
    const infraRepo = `${repoBase}-app-infra`;

    const names: DerivedName[] = [
        {
            kind: 'SOURCE_REPOSITORY',
            name: sourceRepo,
            resourceType: 'CodeCommit repository',
        },
        {
            kind: 'INFRA_REPOSITORY',
            name: infraRepo,
            resourceType: 'CodeCommit repository',
        },
        // A pipeline is named after its REPO, not after the base name:
        // `<repo>-managed-pipeline`. Honoured by all 137 existing pipelines.
        {
            kind: 'SOURCE_PIPELINE',
            name: `${sourceRepo}-managed-pipeline`,
            resourceType: 'CodePipeline pipeline',
        },
        {
            kind: 'INFRA_PIPELINE',
            name: `${infraRepo}-managed-pipeline`,
            resourceType: 'CodePipeline pipeline',
        },
    ];

    // ⚠️ THE STACK AND BUCKET ARE NAMED OFF THE UNSUFFIXED BASE, deliberately.
    // console-api derives them through `infraStackName(appName, env)` and
    // `deploymentBucketName(appName, accountId)` in resource-names.ts, both of
    // which take the application name — NOT the suffixed repository stem. They
    // already carried the stage (`-infra-<env>`) and were the half of this
    // panel that was correct. Folding `repoNameSuffix` in here would break them.
    for (const {stage, accountId} of targetStages) {
        names.push({
            kind: 'CFN_STACK',
            name: `${base}-infra-${stage}`,
            resourceType: 'CloudFormation stack',
            stage,
            accountId,
        });
        names.push({
            kind: 'DEPLOYMENT_BUCKET',
            name: `${base}-infra-${accountId}-ui`,
            resourceType: 'S3 deployment bucket',
            stage,
            accountId,
        });
    }

    return names;
};

// Turn a human-friendly display name into a base name the rules accept.
//
// ⚠️ WHY THIS EXISTS. FNP-1498 made an application's name a display name —
// "Admin Console Localhost" — and the provisioning drawer seeded the base name
// from it directly. The drawer therefore OPENED showing a validation error the
// operator had not caused, against a name they never typed. A suggestion has to
// be something they could actually provision.
//
// This is a SUGGESTION, not a decision: the field stays editable. It returns
// undefined rather than a wrong guess when nothing usable survives, so the
// caller falls back to an empty field instead of a mystery value.
//
// The reserved "-app" suffix is stripped for the same reason `validateBaseName`
// rejects it: deriving appends "-app", so seeding "flexi-testing-app" would
// suggest a name guaranteed to be refused.
export const slugify = (displayName?: string | null): string | undefined => {
    if (!displayName) {
        return undefined;
    }

    let slug = displayName
        .trim()
        .toLowerCase()
        // Anything that is not a valid character becomes a separator, so
        // "Admin Console (Localhost)" does not collapse into one word.
        .replace(/[^a-z0-9]+/g, '-')
        // Collapse and trim the separators those substitutions leave behind.
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    if (slug.endsWith(RESERVED_NAME_SUFFIX)) {
        slug = slug.slice(0, -RESERVED_NAME_SUFFIX.length).replace(/-$/, '');
    }

    // Truncating can strip back to a trailing hyphen, which is itself invalid.
    if (slug.length > MAX_BASE_NAME_LENGTH) {
        slug = slug.slice(0, MAX_BASE_NAME_LENGTH).replace(/-$/, '');
    }

    return slug.length > 0 ? slug : undefined;
};
